/**
 * Ordered-list handling shared by both prompt markdown parsers.
 *
 * The backend parser (`markdown-parser.ts`) and the prompt editor's MarkdownManager each
 * tokenize with marked, and each used to carry its own copy of this pre-pass. The copies
 * drifted the way the serializer's copies did in NEX-2564 and NEX-4602: a fix landed in
 * one and the other kept rewriting prompts. Both now import from here.
 *
 * Deliberately free of `marked`, so the editor can import it through the `@nexus/types`
 * barrel without pulling the parser into the frontend's eager bundle (see the note in
 * `./index.ts`).
 */

// ============================================================================
// Marker values
// ============================================================================

/**
 * Validates that a string is a valid roman numeral (1-99).
 * Rejects false positives like "civil", "mix", "valid" that match [ivxlc]+
 */
export function isValidRomanNumeral(str: string): boolean {
  // Valid roman numerals 1-99: I, II, III, IV, V, VI...X, XI...XL, L, LI...XC, XCI...XCIX
  // NOTE: str.length > 0 is NOT redundant — the regex groups use {0,3} and ?,
  // so both groups can match zero characters, meaning "" would pass the regex alone.
  const validPattern = /^(XC|XL|L?X{0,3})(IX|IV|V?I{0,3})$/i;
  return validPattern.test(str) && str.length > 0;
}

/**
 * Converts a roman numeral string to its integer value.
 */
export function romanToInt(roman: string): number {
  const values: Record<string, number> = { I: 1, V: 5, X: 10, L: 50, C: 100 };
  let total = 0;
  const upper = roman.toUpperCase();
  for (let i = 0; i < upper.length; i++) {
    const char = upper[i];
    const nextChar = upper[i + 1];
    const current = char ? values[char] || 0 : 0;
    const next = nextChar ? values[nextChar] || 0 : 0;
    total += current < next ? -current : current;
  }
  return total;
}

// ============================================================================
// Fences
// ============================================================================

/** An open fenced code block: the character it was opened with, and how many of them. */
interface OpenFence {
  char: string;
  length: number;
}

/** A fence line: optional indentation, a run of 3+ backticks or tildes, then the info string. */
const FENCE_LINE = /^\s*(`{3,}|~{3,})(.*)$/;

/**
 * The fence a line opens, or null when it opens none.
 *
 * A backtick fence's info string may not itself contain a backtick — "```a``` b" is an
 * inline code span, not a fence.
 */
function openingFence(line: string): OpenFence | null {
  const match = line.match(FENCE_LINE);
  const run = match?.[1];
  if (!run) return null;
  const char = run.charAt(0);
  if (char === "`" && (match[2] ?? "").includes("`")) return null;
  return { char, length: run.length };
}

/**
 * Whether a line closes `fence`: the same character, at least as many of it, and nothing
 * after the run but whitespace.
 *
 * Anything shorter is content. That is what lets a block hold a fence of its own — the
 * serializer wraps code that contains ``` in a longer fence precisely so the inner run
 * cannot close it (NEX-4602). Treating every ``` line as a toggle undid that here: the
 * inner run "closed" the block, the code after it was read as prose, and a numbered line
 * in it took a `<!--lt:1-->` marker that nothing strips from code — one more on every
 * save, forever, which is the NEX-2564 cascade again.
 */
function closesFence(line: string, fence: OpenFence): boolean {
  const match = line.match(FENCE_LINE);
  const run = match?.[1];
  if (!run) return false;
  return (
    run.charAt(0) === fence.char && run.length >= fence.length && (match[2] ?? "").trim() === ""
  );
}

// ============================================================================
// List marker normalization
// ============================================================================

/**
 * A line that starts a block of its own — a bullet item, an ATX heading or a blockquote —
 * rather than continuing the paragraph above it.
 */
const BLOCK_START = /^\s*(?:[-*+](?:\s|$)|#{1,6}(?:\s|$)|>)/;

/** Width of a line's leading whitespace. */
function indentWidth(line: string): number {
  return line.length - line.trimStart().length;
}

/**
 * Normalizes non-standard ordered list markers to standard numeric format.
 *
 * marked.js only recognizes numeric ordered lists (1. 2. 3.) and bullet lists (- * +).
 * This function preprocesses markdown to convert lettered and roman numeral lists
 * to numeric format while preserving the original type via an embedded marker.
 *
 * Supported formats:
 * - Uppercase letters: A. B. C. → 1. 2. 3. with <!--lt:A-->
 * - Lowercase letters: a. b. c. → 1. 2. 3. with <!--lt:a-->
 * - Uppercase roman: I. II. III. → 1. 2. 3. with <!--lt:I-->
 * - Lowercase roman: i. ii. iii. → 1. 2. 3. with <!--lt:i-->
 *
 * The marker <!--lt:X--> is embedded at the END of list item content,
 * allowing the parser to restore the correct list type after marked.js tokenizes.
 * The marker must be at the end because marked.js treats <!-- at start as an HTML block.
 *
 * @example
 * Input:  "A. First\nB. Second"
 * Output: "1. First<!--lt:A-->\n2. Second<!--lt:A-->"
 *
 * Input:  "   i. Nested roman"
 * Output: "   1. Nested roman<!--lt:i-->"
 */
export function normalizeListMarkers(markdown: string): string {
  const lines = markdown.split("\n");
  const result: string[] = [];

  // Track list context per indentation level
  // Key: indentation string, Value: { type: 'A'|'a'|'I'|'i'|'1', counter: number, blankLinesBefore: number }
  const listContextByIndent: Map<
    string,
    { type: string; counter: number; blankLinesBefore: number }
  > = new Map();

  // Track blank lines before current line
  let blankLineCount = 0;

  // Track the indentation of the last list item for continuation lines
  // Format: indentation string that continuation lines should use
  let lastListContinuationIndent: string | null = null;

  // The indentation of that item's own marker. A block starting at or left of it is not
  // part of the item, however the item's text ends.
  let lastListMarkerIndent = 0;

  // Inside a fenced code block nothing is a list — the text is literal, and the
  // `<!--lt:X-->` marker below is internal scaffolding the parser strips again once
  // marked has told it the list type. A code block preserves its content verbatim, so
  // a marker injected inside one is never stripped: it survives into the stored text,
  // gets re-injected next to on the next save, and the block grows by 11 characters
  // per save forever (NEX-2564 — a production prompt was accumulating these).
  let openFence: OpenFence | null = null;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (line === undefined) continue;

    if (openFence) {
      if (closesFence(line, openFence)) {
        openFence = null;
        blankLineCount = 0;
        lastListContinuationIndent = null;
      }
      result.push(line);
      continue;
    }

    const fence = openingFence(line);
    if (fence) {
      openFence = fence;
      result.push(line);
      blankLineCount = 0;
      lastListContinuationIndent = null;
      continue;
    }

    // Match line structure: (indentation)(marker)(content)
    // Content can be empty (just whitespace) for empty list items
    const listMatch = line.match(/^(\s*)([A-Za-z]+|\d+)\.(\s+.*?)?$/);

    if (!listMatch) {
      // Not a list line
      if (line.trim() === "") {
        // Blank line - count it and reset continuation tracking
        blankLineCount++;
        lastListContinuationIndent = null;
      } else if (line.startsWith(":::")) {
        // Section or tab directive - resets all list contexts
        listContextByIndent.clear();
        blankLineCount = 0;
        lastListContinuationIndent = null;
      } else if (
        lastListContinuationIndent !== null &&
        !line.startsWith(lastListContinuationIndent) &&
        BLOCK_START.test(line) &&
        indentWidth(line) <= lastListMarkerIndent
      ) {
        // A bullet, heading or blockquote at or left of the item's own marker is not a
        // continuation of it — it is the next block of an enclosing list, or of the
        // document. Re-indenting it below pulled it INTO the item: a nested ordered
        // list's parent swallowed its own next bullet, and a heading after a list became
        // part of the last item. One save moved the content; the next was stable on the
        // result, so no round-trip check could see it (NEX-4602).
        blankLineCount = 0;
        lastListContinuationIndent = null;
      } else if (
        lastListContinuationIndent !== null &&
        !line.startsWith(lastListContinuationIndent)
      ) {
        // Continuation line: text that follows a list item without a blank line separator.
        // This handles multi-line list items (hardBreak / Shift+Enter within a list item).
        // The serializer outputs continuation lines indented to align with the list item content.
        // Here we normalize improperly-indented continuations to the correct indent level.
        // NOTE: A blank line between a list item and the next line resets
        // lastListContinuationIndent to null (above), so regular paragraphs separated by
        // blank lines are NOT affected.
        const trimmed = line.trimStart();
        result.push(lastListContinuationIndent + trimmed);
        blankLineCount = 0;
        // Keep lastListContinuationIndent for potential multi-line continuations
        continue;
      } else {
        // Other content (properly indented continuation, or regular text after blank line)
        blankLineCount = 0;
        // Don't reset lastListContinuationIndent - might be a properly indented continuation
      }
      result.push(line);
      continue;
    }

    const [, indent = "", marker = "", content = ""] = listMatch;

    // Determine marker type
    // Priority: numeric > roman numerals > letters
    // This ensures "i." is treated as roman 1, not letter 9
    let listType: string | null = null;
    let numericValue: number | null = null;

    if (/^\d+$/.test(marker)) {
      // Already numeric - pass through unchanged but track context
      const trimmedContent = content.trimStart();
      const lineWithMarker = `${indent}${marker}. ${trimmedContent}<!--lt:1-->`;

      // Track continuation indentation: indent + length of "N. "
      lastListContinuationIndent = indent + " ".repeat(marker.length + 2);
      lastListMarkerIndent = indent.length;

      // Track context for root-level numeric lists
      if (indent === "") {
        const ctx = listContextByIndent.get(indent);
        if (!ctx || ctx.type !== "1") {
          // New numeric list - save blank lines count
          listContextByIndent.set(indent, {
            type: "1",
            counter: parseInt(marker, 10),
            blankLinesBefore: blankLineCount
          });
        }
      }

      blankLineCount = 0;
      result.push(lineWithMarker);
      continue;
    } else if (isValidRomanNumeral(marker)) {
      // Valid roman numeral (i, ii, iii, iv, v, vi, vii, viii, ix, x, etc.)
      // Check case to determine uppercase vs lowercase
      if (marker === marker.toUpperCase()) {
        listType = "I";
      } else {
        listType = "i";
      }
      numericValue = romanToInt(marker);
    } else if (/^[A-Z]$/.test(marker)) {
      // Single uppercase letter that's NOT a roman numeral (a-h, j-z except l,v,x,c,d,m)
      listType = "A";
      numericValue = marker.charCodeAt(0) - 64; // A=1, B=2, etc.
    } else if (/^[a-z]$/.test(marker)) {
      // Single lowercase letter that's NOT a roman numeral
      listType = "a";
      numericValue = marker.charCodeAt(0) - 96; // a=1, b=2, etc.
    }

    if (listType === null || numericValue === null) {
      // Not a recognized list marker - pass through
      blankLineCount = 0;
      result.push(line);
      continue;
    }

    // Get or create context for this indentation level
    const existingCtx = listContextByIndent.get(indent);
    const isNewList = !existingCtx || existingCtx.type !== listType;

    // Convert to numeric format with type marker at END of content
    // The marker must be at the END because marked.js treats <!-- at start as HTML block
    // For the FIRST item of a new list, encode blank lines count in marker: <!--lt:X:N-->
    const trimmedContent = content.trimStart();
    const typeMarker =
      isNewList && blankLineCount > 0
        ? `<!--lt:${listType}:${blankLineCount}-->`
        : `<!--lt:${listType}-->`;

    // Update context after creating marker
    if (isNewList) {
      listContextByIndent.set(indent, {
        type: listType,
        counter: numericValue,
        blankLinesBefore: blankLineCount
      });
    }

    blankLineCount = 0;

    const normalizedLine = `${indent}${numericValue}. ${trimmedContent}${typeMarker}`;
    result.push(normalizedLine);

    // Track continuation indentation: indent + length of normalized "N. "
    // Use numericValue.toString() to get the actual normalized marker length
    lastListContinuationIndent = indent + " ".repeat(numericValue.toString().length + 2);
    lastListMarkerIndent = indent.length;
  }

  return result.join("\n");
}

// ============================================================================
// Ordered list runs
// ============================================================================

/** One ordered list's worth of marked's list items. */
export interface OrderedListRun<T> {
  /** Counter style: `1`, `a`, `A`, `i` or `I`. */
  type: string;
  /** The number the author gave the run's first item. */
  start: number;
  items: T[];
  /** Blank lines before the run, as encoded by {@link normalizeListMarkers}. */
  blankLinesBefore: number;
}

/** The number an ordered item's marker carries, after {@link normalizeListMarkers}. */
function orderedItemNumber(raw: string): number | null {
  const match = raw.match(/^\s*(\d+)[.)]/);
  return match?.[1] ? parseInt(match[1], 10) : null;
}

/**
 * Splits marked's items for one ordered list into the lists the author wrote.
 *
 * A new list begins where the counter style changes, or where the numbering stops
 * climbing. marked reads `1. 2. 3. 4.` followed directly by `1. 2.` as ONE six-item list —
 * CommonMark only honours the first item's number — so the save renumbered the second
 * list `5. 6.` and the prompt's own references to its steps pointed at the wrong ones.
 * Each run also keeps the number it starts at: `5. 6.` used to come back as `1. 2.`.
 *
 * "Stops climbing" rather than "goes back down", because the serializer writes two
 * adjacent lists that each start at 1 as `1. a` then `1. b`, and a one-item list next
 * to another is common in stored prompts. Merging those renumbered the second on the
 * next save. The rule keeps the numbers the author wrote, which is what the agent reads:
 * a lazily numbered `1. 1. 1.` stays three `1.`s rather than becoming `1. 2. 3.`.
 *
 * @param items - marked's list items, in order
 * @param listTypeOf - the parser's reading of an item's `<!--lt:X-->` marker
 */
export function splitOrderedListRuns<T extends { raw?: string }>(
  items: readonly T[],
  listTypeOf: (raw: string) => { type: string; blankLinesBefore: number }
): OrderedListRun<T>[] {
  const runs: OrderedListRun<T>[] = [];
  let current: OrderedListRun<T> | null = null;
  let previousNumber: number | null = null;

  for (const item of items) {
    const raw = item.raw ?? "";
    const { type, blankLinesBefore } = listTypeOf(raw);
    const number = orderedItemNumber(raw);
    const restarts = number !== null && previousNumber !== null && number <= previousNumber;

    if (!current || current.type !== type || restarts) {
      current = { type, start: number ?? 1, items: [item], blankLinesBefore };
      runs.push(current);
    } else {
      current.items.push(item);
    }
    previousNumber = number;
  }

  return runs;
}
